// ICN Hub Data Types

export interface Resident {
  id: string;
  residentId?: string;
  mrn: string;
  name: string;
  sex?: string;
  unit: string;
  room: string;
  dob?: string;
  dob_raw?: string;
  admitDate?: string;
  active_on_census: boolean;
  physician?: string;
  notes?: string;
  context?: string;
  status?: string;
  payor?: string;
  last_seen_census_at?: string;
  last_missing_census_at?: string | null;
}

export interface ABTRecord {
  id: string;
  residentId?: string;
  abtCourseId?: string;
  record_id?: string;
  mrn: string;
  residentName?: string;
  name?: string;
  unit: string;
  room: string;
  medication?: string;
  med_name?: string;
  dose: string;
  route: string;
  route_raw?: string;
  frequency?: string;
  indication: string;
  infection_source?: string;
  startDate?: string;
  orderDate?: string;
  start_date?: string;
  endDate?: string;
  end_date?: string;
  plannedStopDate?: string;
  status: 'active' | 'completed' | 'discontinued';
  stopReason?: string;
  daysOfTherapy?: number;
  tx_days?: number | null;
  nextReviewDate?: string;
  notes?: string;
  context?: string;
  createdAt?: string;
  updated_at?: string;
  source?: string;
  // F881 Stewardship Fields
  prescriber?: string;
  symptomsChecklist?: string[];
  cultureCollected?: boolean;
  cultureResult?: string;
  cultureReviewedDate?: string;
  timeoutReviewDate?: string;
  timeoutOutcome?: 'continue' | 'change' | 'stop' | null;
  adverseEffects?: string;
  cdiffRisk?: 'low' | 'medium' | 'high';
  stewardshipNotes?: string;
}

export type HAIType = 'CAUTI' | 'CLABSI' | 'MDRO' | 'SSI' | 'Other';
export type DeviceType = 'Urinary catheter' | 'Central line' | 'Other';
export type LabConfirmedStatus = 'Confirmed' | 'Suspected';

export interface IPCase {
  id: string;
  residentId?: string;
  ipCaseId?: string;
  record_id?: string;
  mrn: string;
  residentName?: string;
  name?: string;
  unit: string;
  room: string;
  infectionType?: string;
  syndromeCategory?: SymptomCategory;
  suspectedOrConfirmedOrganism?: string;
  infection_type?: string;
  protocol: 'EBP' | 'Isolation' | 'Standard Precautions';
  isolationType?: 'Contact' | 'Droplet' | 'Airborne';
  isolation_type?: string;
  sourceOfInfection?: string;
  source_of_infection?: string;
  onsetDate?: string;
  onset_date?: string;
  resolutionDate?: string;
  resolution_date?: string;
  status: 'Active' | 'Resolved' | 'Discharged';
  case_status?: string;
  nextReviewDate?: string;
  next_review_date?: string;
  notes?: string;
  context?: string;
  createdAt?: string;
  _autoClosed?: boolean;
  _autoClosedReason?: string;
  // Review tracking fields
  lastReviewDate?: string;
  reviewNotes?: string;
  // F880 Enhanced Fields
  triggerReason?: string;
  highContactCare?: string[];
  signagePosted?: boolean;
  suppliesStocked?: boolean;
  roomCheckDate?: string;
  exposureLinked?: boolean;
  outbreakId?: string;
  requiredPPE?: string;
  staffAssignments?: string;
  closeContacts?: string;
  commonAreasVisited?: string[];
  sharedEquipment?: string[];
  otherEquipment?: string;
  nhsnPathogenCode?: string;
  vaccineStatus?: string;
  dob?: string;

  // Device-associated infections (CAUTI/CLABSI) + MDRO classification
  haiType?: HAIType | '';
  deviceAssociated?: boolean;
  deviceType?: DeviceType | '';
  eventDetectedDate?: string;
  specimenCollectedDate?: string;
  labConfirmedStatus?: LabConfirmedStatus | '';
}

export interface VaxRecord {
  id: string;
  residentId?: string;
  record_id?: string;
  mrn: string;
  residentName?: string;
  name?: string;
  unit: string;
  room: string;
  vaccine: string;
  vaccine_type?: string;
  dose?: string;
  status: 'given' | 'due' | 'overdue' | 'declined' | 'scheduled';
  dateGiven?: string;
  date_given?: string;
  lot?: string;
  site?: string;
  administered_by?: string;
  dueDate?: string;
  due_date?: string;
  notes?: string;
  context?: string;
  createdAt?: string;
  administrationSource?: 'historical' | 'in_house';
  // F883/F887 Enhanced Fields
  offerDate?: string;
  educationProvided?: boolean;
  educationDate?: string;
  educationOutcome?: 'accepted' | 'declined' | 'deferred';
  manufacturer?: string;
  dose_number?: string;
  lotNumber?: string;
  administrationSite?: string;
  declineReason?: string;
  decline_reason?: string;
  medicalExemption?: boolean;
  contraindication?: string;
  consentFormAttached?: boolean;
  nextDueDate?: string;
  seasonOverrideCurrent?: boolean;
  seasonOverrideAt?: string;
}

// Symptom types for auto-classification
export type SymptomCategory = 'respiratory' | 'gi' | 'skin' | 'uti' | 'other';

export interface SymptomEntry {
  id: string;
  name: string;
  category: SymptomCategory;
}

// Predefined symptoms with auto-classification
export const SYMPTOM_OPTIONS: SymptomEntry[] = [
  // Respiratory
  { id: 'fever', name: 'Fever', category: 'respiratory' },
  { id: 'cough', name: 'Cough', category: 'respiratory' },
  { id: 'sob', name: 'Shortness of Breath', category: 'respiratory' },
  { id: 'sore_throat', name: 'Sore Throat', category: 'respiratory' },
  { id: 'congestion', name: 'Congestion', category: 'respiratory' },
  { id: 'runny_nose', name: 'Runny Nose', category: 'respiratory' },
  { id: 'headache', name: 'Headache', category: 'respiratory' },
  { id: 'body_aches', name: 'Body Aches', category: 'respiratory' },
  // GI
  { id: 'nausea', name: 'Nausea', category: 'gi' },
  { id: 'vomiting', name: 'Vomiting', category: 'gi' },
  { id: 'diarrhea', name: 'Diarrhea', category: 'gi' },
  { id: 'abdominal_pain', name: 'Abdominal Pain', category: 'gi' },
  { id: 'loss_appetite', name: 'Loss of Appetite', category: 'gi' },
  // Skin
  { id: 'rash', name: 'Rash', category: 'skin' },
  { id: 'wound_drainage', name: 'Wound Drainage', category: 'skin' },
  { id: 'redness', name: 'Redness/Erythema', category: 'skin' },
  { id: 'swelling', name: 'Swelling', category: 'skin' },
  // UTI
  { id: 'dysuria', name: 'Dysuria', category: 'uti' },
  { id: 'frequency', name: 'Urinary Frequency', category: 'uti' },
  { id: 'urgency', name: 'Urinary Urgency', category: 'uti' },
  { id: 'cloudy_urine', name: 'Cloudy Urine', category: 'uti' },
  // Other
  { id: 'fatigue', name: 'Fatigue', category: 'other' },
  { id: 'confusion', name: 'Confusion/AMS', category: 'other' },
  { id: 'other', name: 'Other', category: 'other' },
];

export interface ClinicalNote {
  id: string;
  type: 'abt_review' | 'ip_assessment' | 'general';
  mrn: string;
  residentName: string;
  related_record_id?: string;
  note_text: string;
  note_date: string;
  author: string;
  createdAt: string;
  updated_at?: string;
}

export interface Note {
  id: string;
  residentId?: string;
  noteId?: string;
  mrn: string;
  residentName?: string;
  name?: string;
  unit: string;
  room: string;
  category: string;
  text: string;
  createdAt: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  // Symptom tracking fields
  symptoms?: string[];
  symptomCategory?: string;
  requiresFollowUp?: boolean;
  followUpDate?: string;
  followUpStatus?: 'pending' | 'completed' | 'escalated';
  followUpNotes?: string;
  linkedLineListingId?: string;
  linkedEntityIds?: {
    abtCourseIds?: string[];
    ipCaseIds?: string[];
    outbreakIds?: string[];
    lineListingRowIds?: string[];
  };
  providerNotified?: boolean;
  providerNotifiedAt?: string;
  providerResponse?: string;
  familyNotified?: boolean;
  familyNotifiedAt?: string;
}

// Line Listing for outbreak tracking
export interface LineListingEntry {
  id: string;
  residentId?: string;
  lineListingRowId?: string;
  mrn: string;
  residentName: string;
  unit: string;
  room: string;
  outbreakId: string;
  onsetDate: string;
  symptoms: string[];
  symptomCategory: SymptomCategory;
  labResults?: string;
  outcome?: 'active' | 'resolved' | 'hospitalized' | 'deceased';
  resolutionDate?: string;
  notes?: string;
  context?: string;
  createdAt: string;
  updatedAt?: string;
  linkedNoteIds?: string[];
  contacts?: ContactEntry[];
  // Extended template data - stores all dynamic field values
  templateData?: Record<string, string | number | boolean | undefined>;
}

export interface LineListingRecommendation {
  id: string;
  abtRecordId: string;
  sourceType?: 'abt' | 'note';
  mrn: string;
  residentName: string;
  unit: string;
  room: string;
  infectionSource: string;
  category: string;
  startDate?: string;
  createdAt: string;
}

// Contact tracing
export interface ContactEntry {
  id: string;
  lineListingId: string;
  contactType: 'resident' | 'staff';
  contactName: string;
  contactMrn?: string; // For residents
  contactRole?: string; // For staff
  contactUnit?: string;
  exposureDate: string;
  exposureType: string; // e.g., "Roommate", "Dining", "Care provided"
  notes?: string;
  context?: string;
  followUpStatus?: 'pending' | 'cleared' | 'symptomatic';
  createdAt: string;
}

// Outbreak definition
export interface Outbreak {
  id: string;
  outbreakId?: string;
  name: string;
  type: SymptomCategory;
  startDate: string;
  endDate?: string;
  status: 'watch' | 'active' | 'resolved';
  declaredAt?: string;
  resolvedAt?: string;
  affectedUnits: string[];
  totalCases: number;
  notes?: string;
  context?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  details: string;
  entityType: 'census' | 'abt' | 'ip' | 'vax' | 'notes' | 'settings' | 'export' | 'import' | 'abx';
  timestamp: string;
  user?: string;
  entityId?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  source?: 'ui' | 'import' | 'api' | 'migration';
}

export interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  userId: string;
  action: 'create' | 'update' | 'delete';
  entity_type: 'resident' | 'ip_case' | 'abt' | 'vax' | 'note';
  entity_id: string;
  entity_name?: string;
  changes?: Record<string, { old: any; new: any }>;
}

export interface HistoryEvent {
  id: string;
  entityType: 'abt' | 'ip' | 'vax';
  entityId: string;
  action: 'created' | 'edited' | 'discontinued' | 'completed' | 'status_changed' | 'reviewed' | 'offered' | 'declined' | 'reoffered' | 'administered';
  occurredAt: string;
  user?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface AppSettings {
  facilityName: string;
  abtReviewCadence: number;
  autoCloseCensus: boolean;
  autoCloseGraceDays: number;
  ipRules: {
    ebpReviewDays: number;
    isolationReviewDays: number;
  };
  vaxRules: Record<string, {
    intervalDays?: number;
    windowStartMmdd?: string;
    windowEndMmdd?: string;
  }>;
  oneDriveBackup?: {
    enabled: boolean;
    folderPath: string;
  };
  lineListingRecommendationDismissals?: string[];
  requireUniqueMrn?: boolean;
  extendedDemographicsEnabled?: boolean;
  residentDaysMethod?: 'midnight_census_sum' | 'adc_x_days';
  averageDailyCensus?: number;
  surveyorRedactionProfile?: {
    hideMRN: boolean;
    hideDOB: boolean;
    hidePhysician: boolean;
    hideNotes: boolean;
    hideNotes: boolean;
    initialsOnly: boolean;
  };
  // Custom report descriptions
  customReportDescriptions?: Record<string, string>;
  // Line listing form field configurations
  admissionNoteTemplates?: {
    isolationStatusLine?: string;
    paperworkReviewLine?: string;
    antibioticStatusLine?: string;
  };
  lineListingConfigs?: Record<string, {
    templateId: string;
    enabledFields: string[];
    customFields?: Array<{
      id: string;
      label: string;
      shortLabel?: string;
      type: 'text' | 'checkbox' | 'select' | 'date' | 'number';
      options?: string[];
      category?: string;
      defaultEnabled: boolean;
    }>;
  }>;
}

export interface AppDatabase {
  census: {
    residentsByMrn: Record<string, Resident>;
    residentsById?: Record<string, Resident>;
    lastImportAt?: string;
    meta?: {
      imported_at: string | null;
    };
  };
  records: {
    abx: ABTRecord[];
    ip_cases: IPCase[];
    vax: VaxRecord[];
    notes: Array<Note | ClinicalNote>;
    line_listings: LineListingEntry[];
    outbreaks: Outbreak[];
    contacts: ContactEntry[];
    history?: HistoryEvent[];
  };
  audit_log: AuditEntry[];
  workflow_metrics?: Array<{
    id: string;
    eventName: string;
    view: string;
    residentId?: string;
    mrn?: string;
    timestamp: string;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }>;
  settings: AppSettings;
  meta?: {
    schemaVersion?: number;
    residentIdByMrn?: Record<string, string>;
  };
}

export type ViewType = 
  | 'dashboard' 
  | 'abt' 
  | 'census' 
  | 'resident_overview'
  | 'ip' 
  | 'vax' 
  | 'notes' 
  | 'outbreak'
  | 'reference_library'
  | 'reports' 
  | 'audit'
  | 'user_management'
  | 'settings'
  | 'staff' 
  | 'staff_vaccination';

export interface NavItem {
  id: ViewType;
  label: string;
  icon: string;
}

export type ResidentFilterMode =
  | 'active_only'
  | 'active_in_period'
  | 'all';

export interface ResidentFilterConfig {
  mode: ResidentFilterMode;
  dateRange?: { fromDate: string; toDate: string };
  showDischargedLabel: boolean;
}

export interface ReportFilters {
  residentFilter?: ResidentFilterConfig;
  [key: string]: unknown;
}

export type ReportCategory =
  | 'daily_operations'
  | 'compliance'
  | 'surveillance'
  | 'vaccination'
  | 'stewardship'
  | 'custom'
  | 'staff_employee';

export type DataSource =
  | 'census'
  | 'abt'
  | 'ip_cases'
  | 'vaccinations'
  | 'notes'
  | 'line_listings'
  | 'outbreaks'
  | 'contacts'
  | 'staff'
  | 'staffVaccination';

export interface ColumnFormat {
  type: 'date' | 'currency' | 'percentage' | 'duration';
  pattern?: string;
}

export type ColumnTransform =
  | 'uppercase'
  | 'lowercase'
  | 'titlecase'
  | 'calculate_duration'
  | 'calculate_age'
  | 'infer_class';

export interface ColumnDefinition {
  id: string;
  fieldPath: string;
  displayName: string;
  dataType: 'text' | 'number' | 'date' | 'boolean' | 'status';
  width?: number;
  alignment?: 'left' | 'center' | 'right';
  format?: ColumnFormat;
  transform?: ColumnTransform;
  required?: boolean;
  position: number;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ReportLayoutConfig {
  orientation: 'portrait' | 'landscape';
  pageSize: 'letter' | 'legal' | 'a4';
  fontSize: 'compact' | 'normal' | 'large';
  includeSummary?: boolean;
}

export interface ReportFilterConfig {
  [key: string]: string | number | boolean | undefined;
}

export interface CustomReportTemplate {
  id: string;
  name: string;
  description?: string;
  category: ReportCategory;
  dataSource: DataSource;
  selectedColumns: ColumnDefinition[];
  filters: ReportFilterConfig;
  sorting?: SortConfig;
  layout: ReportLayoutConfig;
  createdAt: string;
  isProtected?: boolean;
  isPinned?: boolean;
}
